import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export const eventsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "eventsApi",
    baseUrl: "/api/events",
  }),
  reducerPath: "eventsApi",
  tagTypes: ["Events", "EventNotes"],
  endpoints: (builder) => ({
    getEvents: builder.query<
      any,
      {
        id?: string;
        include?: string;
        limit?: number;
        /** Filter to a single event type (e.g. "TOUR"). Case-insensitive. */
        type?: string;
        /**
         * REP-200 §4 — when set, server walks the deal's project tree
         * (deal + child PURSUIT projects) and returns events linked to
         * any of them. The Tours widget on a parent deal uses this.
         */
        dealId?: string;
      }
    >({
      query: (args) => {
        const params = new URLSearchParams();

        if (args.include) {
          params.append("include", args.include);
        }

        if (args.limit) {
          params.append("limit", args.limit?.toString());
        }

        if (args.type) {
          params.append("type", args.type);
        }

        if (args.dealId) {
          params.append("dealId", args.dealId);
        }

        return `${args.id ? `/${args.id}` : ""}?${params.toString()}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Events" }],
    }),
    createEvent: builder.mutation<any, { data: any }>({
      query: ({ data }) => ({
        url: "/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Events" }],
    }),
    updateEvent: builder.mutation<
      any,
      {
        id: string;
        name?: string;
        description?: string;
        status?: string;
        startDateTime?: string;
        endDateTime?: string;
        metadata?: Record<string, any> | null;
        /**
         * TOUR sibling shortcut: when true, the backend treats this
         * update as "accept this slot" — flips THIS sibling to SCHEDULED
         * and every other PROPOSED sibling in the bundle to CANCELLED.
         * (Equivalent to passing status='SCHEDULED' from PROPOSED.)
         */
        acceptSlot?: boolean;
      }
    >({
      query: ({ id, ...data }) => ({
        // PATCH is the ticket-spec'd verb for tour-side mutations;
        // backend also accepts PUT for backcompat.
        url: `/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [{ type: "Events" }],
    }),
    addEventParticipants: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/${id}/participants`,
        method: "POST",
        body: data,
      }),
    }),
    removeEventParticipants: builder.mutation<
      any,
      { id: string; personId: string }
    >({
      query: ({ id, personId }) => ({
        url: `/${id}/participants/${personId}`,
        method: "DELETE",
      }),
    }),
    addEventProperties: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/${id}/properties`,
        method: "POST",
        body: data,
      }),
    }),
    removeEventProperties: builder.mutation<
      any,
      { id: string; propertyId: string }
    >({
      query: ({ id, propertyId }) => ({
        url: `/${id}/properties/${propertyId}`,
        method: "DELETE",
      }),
    }),
    deleteEvent: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),
    /**
     * Re-send the host proposal email for a tour bundle ("nudge").
     * Endpoint owned by Agent E1; this mutation may 404 until it lands.
     * Backend returns 409 if the bundle has no PROPOSED sibling to nudge.
     */
    nudgeTour: builder.mutation<any, { bundleId: string }>({
      query: ({ bundleId }) => ({
        url: `/${bundleId}/nudge`,
        method: "POST",
      }),
    }),

    // REP-206 §6 — tour notes (notes attached to an Event via EventNote).
    getEventNotes: builder.query<any, { eventId: string }>({
      query: ({ eventId }) => `/${eventId}/notes`,
      providesTags: (_result, _error, { eventId }) => [
        { type: "EventNotes", id: eventId },
      ],
    }),
    addEventNote: builder.mutation<any, { eventId: string; text: string }>({
      query: ({ eventId, text }) => ({
        url: `/${eventId}/notes`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: "EventNotes", id: eventId },
        // The note count rides on event reads, so refresh those too.
        { type: "Events" },
      ],
    }),
    updateEventNote: builder.mutation<
      any,
      { eventId: string; noteId: string; text: string }
    >({
      query: ({ eventId, noteId, text }) => ({
        url: `/${eventId}/notes/${noteId}`,
        method: "PATCH",
        body: { text },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: "EventNotes", id: eventId },
        { type: "Events" },
      ],
    }),
    deleteEventNote: builder.mutation<
      any,
      { eventId: string; noteId: string }
    >({
      query: ({ eventId, noteId }) => ({
        url: `/${eventId}/notes/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: "EventNotes", id: eventId },
        { type: "Events" },
      ],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useLazyGetEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useAddEventParticipantsMutation,
  useRemoveEventParticipantsMutation,
  useAddEventPropertiesMutation,
  useRemoveEventPropertiesMutation,
  useDeleteEventMutation,
  useNudgeTourMutation,
  useGetEventNotesQuery,
  useAddEventNoteMutation,
  useUpdateEventNoteMutation,
  useDeleteEventNoteMutation,
} = eventsApi;
